// src/pages/orcamentos/OrcamentoConsultaPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaFilePdf, FaSortDown, FaSortUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/input/Input';
import { useFilial } from '../../contexts/administrativo/FilialContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useOrcamento } from '../../contexts/orcamentos/OrcamentoContext';
import { useCliente } from '../../contexts/pessoas/ClienteContext';
import { dataHoraOrcamento, moedaOrcamento, SITUACOES_ORCAMENTO } from '../../models/orcamentos/Orcamento';
import '../servicos/ordensServico/Servicos.css';

const OrcamentoConsultaPage = () => {
  const api = useOrcamento();
  const { filialLogin } = useFilial();
  const { clientes, showClienteSearch } = useCliente();
  const { showMessage } = useMessage();
  const navigate = useNavigate();
  const filialRef = useRef(null);
  const [filtros, setFiltros] = useState({ numero: '', clienteId: '', clienteNome: '', situacao: '', dataInicial: '', dataFinal: '' });
  const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
  const alterar = (campo, valor) => setFiltros(anterior => ({ ...anterior, [campo]: valor }));
  const selecionarCliente = cliente => setFiltros(anterior => ({ ...anterior, clienteId: cliente?.id || '', clienteNome: cliente?.pessoa?.nome || cliente?.nome || '' }));
  const validarCliente = id => {
    if (!id) { selecionarCliente(null); return; }
    const cliente = clientes.find(x => Number(x.id) === Number(id));
    if (cliente) selecionarCliente(cliente);
    else { showMessage('Cliente não encontrado.', 'warning'); selecionarCliente(null); }
  };
  const buscarCliente = async () => selecionarCliente(await showClienteSearch());
  const buscar = () => api.listar({ filialId: filialLogin?.id, numero: filtros.numero || undefined, clienteId: filtros.clienteId || undefined, situacao: filtros.situacao || undefined, dataInicial: filtros.dataInicial || undefined, dataFinal: filtros.dataFinal || undefined });

  const orcamentosOrdenados = useMemo(() => {
    const { campo, direcao } = ordenacao;
    if (!campo) return api.orcamentos;

    const multiplicador = direcao === 'asc' ? 1 : -1;
    return [...api.orcamentos].sort((a, b) => {
      const valorA = campo === 'situacaoDescricao' ? SITUACOES_ORCAMENTO[a.situacao] : a?.[campo];
      const valorB = campo === 'situacaoDescricao' ? SITUACOES_ORCAMENTO[b.situacao] : b?.[campo];

      if (['numero', 'valorTotal'].includes(campo)) {
        return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
      }
      if (campo === 'dataCriacao') {
        return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
      }
      return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
        sensitivity: 'base',
      }) * multiplicador;
    });
  }, [api.orcamentos, ordenacao]);

  const alterarOrdenacao = campo => {
    setOrdenacao(atual => ({
      campo,
      direcao: atual.campo === campo && atual.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderOrdenadorColuna = (campo, titulo, style = {}) => {
    const ativa = ordenacao.campo === campo;
    return (
      <button
        type="button"
        onClick={() => alterarOrdenacao(campo)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          fontWeight: 'bold',
          color: 'inherit',
          cursor: 'pointer',
          width: '100%',
          textAlign: style.textAlign || 'left',
        }}
      >
        {titulo}
        {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
      </button>
    );
  };

  useEffect(() => { if (filialLogin?.id && filialRef.current !== filialLogin.id) { filialRef.current = filialLogin.id; buscar(); } }, [filialLogin?.id]);

  return <div className="servicos-page container-fluid py-3">
    <div className="d-flex justify-content-between align-items-center mb-3"><h3>Orçamentos</h3><button type="button" className="btn btn-primary" onClick={() => navigate('/orcamento')}>Novo orçamento</button></div>
    <section className="servicos-card">
      <div className="d-flex flex-wrap align-items-end gap-2">
        <Input label="Número" value={filtros.numero} size={100} onChange={e => alterar('numero', e.target.value)} />
        <Input label="Cliente" value={filtros.clienteId} size={100} btnBuscar={buscarCliente} onChange={e => alterar('clienteId', e.target.value)} onBlur={e => validarCliente(e.target.value)} />
        <Input label="Nome do cliente" value={filtros.clienteNome} disabled size={280} />
        <div><label className="form-label">Situação</label><select className="form-select" value={filtros.situacao} onChange={e => alterar('situacao', e.target.value)}><option value="">Todas</option>{Object.entries(SITUACOES_ORCAMENTO).map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}</select></div>
        <Input label="Data inicial" type="date" value={filtros.dataInicial} size={150} onChange={e => alterar('dataInicial', e.target.value)} />
        <Input label="Data final" type="date" value={filtros.dataFinal} size={150} onChange={e => alterar('dataFinal', e.target.value)} />
        <button type="button" className="btn btn-outline-primary mb-1" disabled={api.loading} onClick={buscar}>{api.loading ? 'Buscando...' : 'Buscar'}</button>
      </div>
    </section>
    <section className="servicos-card mt-3"><div className="table-responsive"><table className="table table-hover align-middle"><thead><tr><th>{renderOrdenadorColuna('numero', 'Número')}</th><th>{renderOrdenadorColuna('clienteNome', 'Cliente')}</th><th>{renderOrdenadorColuna('equipamentoDescricao', 'Equipamento')}</th><th>{renderOrdenadorColuna('situacaoDescricao', 'Situação')}</th><th>{renderOrdenadorColuna('dataCriacao', 'Criação')}</th><th className="text-end">{renderOrdenadorColuna('valorTotal', 'Total', { textAlign: 'right' })}</th><th /></tr></thead><tbody>
      {orcamentosOrdenados.map(orcamento => <tr key={orcamento.id} role="button" onDoubleClick={() => navigate(`/orcamento/${orcamento.id}`)}><td>{String(orcamento.numero).padStart(6, '0')}</td><td>{orcamento.clienteNome}</td><td>{orcamento.equipamentoDescricao || '-'}</td><td><span className={`badge ${Number(orcamento.situacao) === 1 ? 'bg-warning text-dark' : Number(orcamento.situacao) === 2 ? 'bg-success' : 'bg-danger'}`}>{SITUACOES_ORCAMENTO[orcamento.situacao]}</span></td><td>{dataHoraOrcamento(orcamento.dataCriacao)}</td><td className="text-end">{moedaOrcamento(orcamento.valorTotal)}</td><td className="text-end"><button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => api.imprimir(orcamento.id)}><FaFilePdf /></button><button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(`/orcamento/${orcamento.id}`)}>Abrir</button></td></tr>)}
      {!api.orcamentos.length && <tr><td colSpan="7" className="text-center text-muted">Nenhum orçamento encontrado.</td></tr>}
    </tbody></table></div></section>
  </div>;
};

export default OrcamentoConsultaPage;
